import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { useCustomerAppConfig } from "undercarriage";
import { CODAT_SUPPORTED_ACCOUNTING } from "shared-ui";

import { useFundingApplication, useFundingApplicationMutation } from "../../../queries/funding-application/queries";
import { usePlatformConnectors } from "../../../queries/connectors/queries";
import { ACCOUNTING_CONNECTOR_SLUGS } from "../../../components/constants";

// Handles Codat callback URLs directly — ConnectionAuthCallbackHandler skips Codat
// connectors (no complete-connection POST), so onSuccess never fires for them.
export const CodatConnectionCallbackHandler = () => {
  const [searchParams] = useSearchParams();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const { data: connectorsWithConnections } = usePlatformConnectors(customerDomain);
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);

  // Prevents duplicate mutations if the component re-renders before the URL clears.
  const processedSlugRef = useRef<string | null>(null);

  useEffect(() => {
    const slug = searchParams.get("complete-connection");
    if (!slug) {
      processedSlugRef.current = null;
      return;
    }
    if (!ACCOUNTING_CONNECTOR_SLUGS.includes(slug) || processedSlugRef.current === slug) {
      return;
    }
    if (searchParams.get("oauth-error")) {
      return;
    }
    processedSlugRef.current = slug;

    const codatConnector = connectorsWithConnections.find((c) => c.slug === slug);
    const identifierName = codatConnector?.connections[0]?.identifierName ?? "";
    const matchedPlatform = CODAT_SUPPORTED_ACCOUNTING.find((platform) =>
      identifierName.toLowerCase().startsWith(platform.toLowerCase())
    );

    const currentSlugs = fundingApplication.selectedConnectorSlugs ?? [];
    const currentUnsupportedAccounting = fundingApplication.selectedUnsupportedAccounting ?? [];
    fundingApplicationMutation.mutate({
      id: fundingApplication.id,
      selectedConnectorSlugs: currentSlugs.includes(slug) ? undefined : [...currentSlugs, slug],
      accountingDocumentsRequired: false,
      ...(matchedPlatform && !currentUnsupportedAccounting.includes(matchedPlatform)
        ? { selectedUnsupportedAccounting: [...currentUnsupportedAccounting, matchedPlatform] }
        : {}),
    });
  }, [
    searchParams,
    fundingApplication.id,
    fundingApplication.selectedConnectorSlugs,
    fundingApplication.selectedUnsupportedAccounting,
    fundingApplicationMutation,
    connectorsWithConnections,
  ]);

  return null;
};
